import { Routes, Route, Navigate } from "react-router-dom";

import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import UnavailableKBProtectedRoute from "./firebase/unavailable-kb-protected-route.comp";
import TrialExpiredProtectedRoute from "./firebase/trial-expired-protected-route.comp";
import UnverifiedProtectedRoute from "./firebase/unverified-protected-route.comp";
import VerifiedProtectedRoute from "./firebase/verified-protected-route.comp";
import ManagerProtectedRoute from "./firebase/manager-protected-route.comp";
import AdminProtectedRoute from "./firebase/admin-protected-route.comp";
import FeatureProtectedRoute from "./firebase/feature-protected-route.comp";
import SuperAdminProtectedRoute from "./firebase/super-admin-protected-route.comp";
import VerifiedSignInRoute from "../routes/verified-signin-route.comp";
import ProtectedRoute from "./protected-route.comp";
import GeneralMenuLayout from "../layout/general-menu-layout.comp";
import HomeRedirect from "./home-redirect.comp";
import SupportSessionView from "../../views/auth/support-session/support-session";
import ForgotPassword from "../../views/auth/forgot-password/forgot-password";
import ChangePassword from "../../views/auth/change-password/change-password";
import EmaiConfirm from "../../views/auth/email-confirm/email-confirm";
import EmaiConfirmed from "../../views/auth/email-confirmed/email-confirmed";
import AuthCallbackHandler from "../auth-callback/auth-callback-handler.comp";
import ChatBot from "../../views/chat-bot/chat-bot";
import NotFound from "../../views/not-found/not-found";
import ErrorPage from "../../views/error-page/error-page";
import GooglePickerPage from "../../views/auth/google-picker/google-picker";
import KnowledgeBaseElements from "../../views/admin/knowledge-base-elements/knowledge-base-elements";
import ReviewCrawlView from "../../views/admin/review-crawl/review-crawl";
import RecognitionResultView from "../../views/admin/recognition-result/recognition-result";
import CompareRecognitionResultView from "../../views/admin/compare-recognition-result/compare-recognition-result";
import TrialKnowledgeBaseView from "../../views/admin/trial-knowledge-base/trial-knowledge-base";
import ChatIntegrationsView from "../../views/admin/chat-integrations/chat-integrations";
import IntegrationsListView from "../../views/admin/integrations/integrations-list.view";
import AgentsView from "../../views/admin/agents/agents.view";
import AssistantsView, { RedirectToAssistants } from "../../views/admin/assistants/assistants.view";
import SwitchScreenView from "../../views/admin/switch-screen/switch-screen";
import DashboardView from "../../views/admin/dashboard/dashboard";
import TenantManagement from "../../views/admin/tenant-management/tenant-management";
import TenantAuthenticationView from "../../views/admin/tenant-authentication/tenant-authentication";
import UserManagementView from "../../views/admin/user-management/user-management";
import KnowledgeBaseUnavailableView from "../../views/knowledge-base-unavailable/knowledge-base-unavailable";
import TrialUpgradeView from "../../views/trial-upgrade/trial-upgrade";

export const EmptyRoutes = () => {
    return (
        <Routes>
            <Route path="*" element={<></>} />
        </Routes>
    );
};

enum RouteType {
    default,
    trial
}

// `type` no longer selects anything here: the trial/default registration
// pages were tenant-host forms that never rendered (see /registration below).
const AssistantRoutes = (_type: RouteType) => {
    return (
        <Routes>
            {/* Tenant hosts never render a sign-in or sign-up form: VerifiedSignInRoute
                redirects both routes to the login domain (auth-domain-routes). */}
            <Route
                path="/registration"
                element={<VerifiedSignInRoute>{null}</VerifiedSignInRoute>}
            />
            <Route path="/" element={<Navigate to="/new-chat" />} />
            <Route
                path="/new-chat"
                element={
                    <VerifiedProtectedRoute>
                        <ChatBot isNewChat={true} />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/chat/:chatId"
                element={
                    <VerifiedProtectedRoute>
                        <ChatBot />
                    </VerifiedProtectedRoute>
                }
            />
            <Route path="/login" element={<VerifiedSignInRoute>{null}</VerifiedSignInRoute>} />
            <Route path="/support-session" element={<SupportSessionView />} />
            <Route
                path="/change-password"
                element={
                    <ProtectedRoute>
                        <ChangePassword />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <VerifiedSignInRoute>
                        <ForgotPassword />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/email-confirm"
                element={
                    <UnverifiedProtectedRoute>
                        <EmaiConfirm />
                    </UnverifiedProtectedRoute>
                }
            />
            <Route
                path="/registration-success"
                element={
                    <VerifiedProtectedRoute>
                        <EmaiConfirmed />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/knowledge-base-unavailable"
                element={
                    <UnavailableKBProtectedRoute>
                        <KnowledgeBaseUnavailableView />
                    </UnavailableKBProtectedRoute>
                }
            />
            <Route
                path="/trial-upgrade"
                element={
                    <TrialExpiredProtectedRoute>
                        <TrialUpgradeView />
                    </TrialExpiredProtectedRoute>
                }
            />
            <Route path="/auth/callback" element={<AuthCallbackHandler />} />
            <Route path="/google-picker" element={<GooglePickerPage />} />
            <Route path="/error" element={<ErrorPage />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export const DefaultAssistantRoutes = () => {
    return AssistantRoutes(RouteType.default);
};

export const TrialAssistantRoutes = () => {
    return AssistantRoutes(RouteType.trial);
};

const AdminRoutes = (type: RouteType) => {
    return (
        <Routes>
            {!isMobileDevice() ? (
                <>
                    {type === RouteType.trial ? (
                        <>
                            <Route
                                path="/"
                                element={
                                    <VerifiedProtectedRoute>
                                        <GeneralMenuLayout>
                                            <DashboardView />
                                        </GeneralMenuLayout>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <TrialKnowledgeBaseView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId"
                                element={<Navigate to="/knowledge-base" />}
                            />
                            <Route
                                path="/knowledge-base/:providerId/:courseId"
                                element={<Navigate to="/knowledge-base" />}
                            />
                        </>
                    ) : (
                        <>
                            <Route
                                path="/"
                                element={
                                    <VerifiedProtectedRoute>
                                        <HomeRedirect />
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <DashboardView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <KnowledgeBaseElements />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <KnowledgeBaseElements />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId/:courseId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <KnowledgeBaseElements />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId/review/:runId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <ReviewCrawlView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/compare-recognition-result/:providerId/:uid"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <CompareRecognitionResultView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                        </>
                    )}
                    <Route
                        path="/user-management"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <GeneralMenuLayout>
                                        <UserManagementView />
                                    </GeneralMenuLayout>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route path="/prompt-tuning" element={<RedirectToAssistants tab="prompt" />} />
                    <Route
                        path="/tenant-management"
                        element={
                            <VerifiedProtectedRoute>
                                <SuperAdminProtectedRoute>
                                    <GeneralMenuLayout>
                                        <TenantManagement />
                                    </GeneralMenuLayout>
                                </SuperAdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/search-configuration"
                        element={<RedirectToAssistants tab="retrieval" />}
                    />
                    <Route
                        path="/assistant-settings"
                        element={<RedirectToAssistants tab="presentation" />}
                    />
                    <Route
                        path="/recognition-result/:providerId/:uid"
                        element={
                            <VerifiedProtectedRoute>
                                <ManagerProtectedRoute>
                                    <GeneralMenuLayout>
                                        <RecognitionResultView />
                                    </GeneralMenuLayout>
                                </ManagerProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/manage-invites"
                        element={<RedirectToAssistants tab="invites" />}
                    />
                    <Route
                        path="/chat-integrations"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="chatIntegrations">
                                        <GeneralMenuLayout>
                                            <ChatIntegrationsView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/integrations"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="embedIntegrations">
                                        <GeneralMenuLayout>
                                            <IntegrationsListView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    {/* D8 (Phase C): managers reach the list; rows are scoped to
                        the Assistants they manage and dialog tabs gate by role. */}
                    <Route
                        path="/assistants"
                        element={
                            <VerifiedProtectedRoute>
                                <ManagerProtectedRoute>
                                    <GeneralMenuLayout>
                                        <AssistantsView />
                                    </GeneralMenuLayout>
                                </ManagerProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/agents"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="agents">
                                        <GeneralMenuLayout>
                                            <AgentsView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    {/* tenant_auth_delegation_design.md §4.1: the tenant's own
                        Authentication settings for granted admins. */}
                    <Route
                        path="/authentication"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="authSettings">
                                        <GeneralMenuLayout>
                                            <TenantAuthenticationView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    {/* Create/edit are now modal-driven from the list; keep old
                        deep links working by redirecting to the list. */}
                    <Route
                        path="/integrations/new"
                        element={<Navigate to="/integrations" replace />}
                    />
                    <Route
                        path="/integrations/:id"
                        element={<Navigate to="/integrations" replace />}
                    />
                    <Route path="/customization" element={<RedirectToAssistants tab="profile" />} />
                    <Route path="*" element={<NotFound />} />
                </>
            ) : (
                <>
                    <Route
                        path="*"
                        element={
                            <VerifiedProtectedRoute>
                                <GeneralMenuLayout>
                                    <SwitchScreenView />
                                </GeneralMenuLayout>
                            </VerifiedProtectedRoute>
                        }
                    />
                </>
            )}
            {/* Tenant hosts never render a sign-in or sign-up form: VerifiedSignInRoute
                redirects both routes to the login domain (auth-domain-routes). */}
            <Route
                path="/registration"
                element={<VerifiedSignInRoute>{null}</VerifiedSignInRoute>}
            />
            <Route
                path="/email-confirm"
                element={
                    <UnverifiedProtectedRoute>
                        <EmaiConfirm />
                    </UnverifiedProtectedRoute>
                }
            />
            <Route path="/login" element={<VerifiedSignInRoute>{null}</VerifiedSignInRoute>} />
            <Route path="/support-session" element={<SupportSessionView />} />
            <Route
                path="/change-password"
                element={
                    <ProtectedRoute>
                        <ChangePassword />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <VerifiedSignInRoute>
                        <ForgotPassword />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/registration-success"
                element={
                    <VerifiedProtectedRoute>
                        <EmaiConfirmed />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/knowledge-base-unavailable"
                element={
                    <UnavailableKBProtectedRoute>
                        <KnowledgeBaseUnavailableView />
                    </UnavailableKBProtectedRoute>
                }
            />
            <Route
                path="/trial-upgrade"
                element={
                    <TrialExpiredProtectedRoute>
                        <GeneralMenuLayout>
                            <TrialUpgradeView />
                        </GeneralMenuLayout>
                    </TrialExpiredProtectedRoute>
                }
            />
            <Route path="/auth/callback" element={<AuthCallbackHandler />} />
            <Route path="/google-picker" element={<GooglePickerPage />} />
            <Route path="/error" element={<ErrorPage />} />
        </Routes>
    );
};

export const DefaultAdminRoutes = () => {
    return AdminRoutes(RouteType.default);
};

export const TrialAdminRoutes = () => {
    return AdminRoutes(RouteType.trial);
};

const CombinedRoutes = (type: RouteType) => {
    return (
        <Routes>
            {!isMobileDevice() ? (
                <>
                    {type === RouteType.trial ? (
                        <>
                            <Route
                                path="/"
                                element={
                                    <VerifiedProtectedRoute>
                                        <HomeRedirect />
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <DashboardView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <TrialKnowledgeBaseView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId"
                                element={<Navigate to="/knowledge-base" />}
                            />
                            <Route
                                path="/knowledge-base/:providerId/:courseId"
                                element={<Navigate to="/knowledge-base" />}
                            />
                        </>
                    ) : (
                        <>
                            <Route
                                path="/"
                                element={
                                    <VerifiedProtectedRoute>
                                        <HomeRedirect />
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <DashboardView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <KnowledgeBaseElements />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <KnowledgeBaseElements />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId/:courseId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <KnowledgeBaseElements />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge-base/:providerId/review/:runId"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <ReviewCrawlView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                            <Route
                                path="/compare-recognition-result/:providerId/:uid"
                                element={
                                    <VerifiedProtectedRoute>
                                        <ManagerProtectedRoute>
                                            <GeneralMenuLayout>
                                                <CompareRecognitionResultView />
                                            </GeneralMenuLayout>
                                        </ManagerProtectedRoute>
                                    </VerifiedProtectedRoute>
                                }
                            />
                        </>
                    )}
                    <Route
                        path="/user-management"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <GeneralMenuLayout>
                                        <UserManagementView />
                                    </GeneralMenuLayout>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route path="/prompt-tuning" element={<RedirectToAssistants tab="prompt" />} />
                    <Route
                        path="/tenant-management"
                        element={
                            <VerifiedProtectedRoute>
                                <SuperAdminProtectedRoute>
                                    <GeneralMenuLayout>
                                        <TenantManagement />
                                    </GeneralMenuLayout>
                                </SuperAdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/search-configuration"
                        element={<RedirectToAssistants tab="retrieval" />}
                    />
                    <Route
                        path="/assistant-settings"
                        element={<RedirectToAssistants tab="presentation" />}
                    />
                    <Route
                        path="/recognition-result/:providerId/:uid"
                        element={
                            <VerifiedProtectedRoute>
                                <ManagerProtectedRoute>
                                    <GeneralMenuLayout>
                                        <RecognitionResultView />
                                    </GeneralMenuLayout>
                                </ManagerProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/manage-invites"
                        element={<RedirectToAssistants tab="invites" />}
                    />
                    <Route
                        path="/chat-integrations"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="chatIntegrations">
                                        <GeneralMenuLayout>
                                            <ChatIntegrationsView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/integrations"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="embedIntegrations">
                                        <GeneralMenuLayout>
                                            <IntegrationsListView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    {/* D8 (Phase C): managers reach the list; rows are scoped to
                        the Assistants they manage and dialog tabs gate by role. */}
                    <Route
                        path="/assistants"
                        element={
                            <VerifiedProtectedRoute>
                                <ManagerProtectedRoute>
                                    <GeneralMenuLayout>
                                        <AssistantsView />
                                    </GeneralMenuLayout>
                                </ManagerProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    <Route
                        path="/agents"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="agents">
                                        <GeneralMenuLayout>
                                            <AgentsView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    {/* tenant_auth_delegation_design.md §4.1: the tenant's own
                        Authentication settings for granted admins. */}
                    <Route
                        path="/authentication"
                        element={
                            <VerifiedProtectedRoute>
                                <AdminProtectedRoute>
                                    <FeatureProtectedRoute feature="authSettings">
                                        <GeneralMenuLayout>
                                            <TenantAuthenticationView />
                                        </GeneralMenuLayout>
                                    </FeatureProtectedRoute>
                                </AdminProtectedRoute>
                            </VerifiedProtectedRoute>
                        }
                    />
                    {/* Create/edit are now modal-driven from the list; keep old
                        deep links working by redirecting to the list. */}
                    <Route
                        path="/integrations/new"
                        element={<Navigate to="/integrations" replace />}
                    />
                    <Route
                        path="/integrations/:id"
                        element={<Navigate to="/integrations" replace />}
                    />
                    <Route path="/customization" element={<RedirectToAssistants tab="profile" />} />
                    <Route path="*" element={<NotFound />} />
                </>
            ) : (
                <>
                    {/* On mobile, desktop-only routes are not registered; anything
                        that isn't a mobile-supported route (chat + auth, declared
                        below) falls through to here and defaults to new-chat. */}
                    <Route path="*" element={<Navigate to="/new-chat" replace />} />
                </>
            )}
            {/* Chat routes - outside mobile check so they work on all devices */}
            <Route
                path="/new-chat"
                element={
                    <VerifiedProtectedRoute>
                        <ChatBot isNewChat={true} />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/chat/:chatId"
                element={
                    <VerifiedProtectedRoute>
                        <ChatBot />
                    </VerifiedProtectedRoute>
                }
            />
            {/* Tenant hosts never render a sign-in or sign-up form: VerifiedSignInRoute
                redirects both routes to the login domain (auth-domain-routes). */}
            <Route
                path="/registration"
                element={<VerifiedSignInRoute>{null}</VerifiedSignInRoute>}
            />
            <Route
                path="/email-confirm"
                element={
                    <UnverifiedProtectedRoute>
                        <EmaiConfirm />
                    </UnverifiedProtectedRoute>
                }
            />
            <Route path="/login" element={<VerifiedSignInRoute>{null}</VerifiedSignInRoute>} />
            <Route path="/support-session" element={<SupportSessionView />} />
            <Route
                path="/change-password"
                element={
                    <ProtectedRoute>
                        <ChangePassword />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <VerifiedSignInRoute>
                        <ForgotPassword />
                    </VerifiedSignInRoute>
                }
            />
            <Route
                path="/registration-success"
                element={
                    <VerifiedProtectedRoute>
                        <EmaiConfirmed />
                    </VerifiedProtectedRoute>
                }
            />
            <Route
                path="/knowledge-base-unavailable"
                element={
                    <UnavailableKBProtectedRoute>
                        <KnowledgeBaseUnavailableView />
                    </UnavailableKBProtectedRoute>
                }
            />
            <Route
                path="/trial-upgrade"
                element={
                    <TrialExpiredProtectedRoute>
                        <GeneralMenuLayout>
                            <TrialUpgradeView />
                        </GeneralMenuLayout>
                    </TrialExpiredProtectedRoute>
                }
            />
            <Route path="/auth/callback" element={<AuthCallbackHandler />} />
            <Route path="/google-picker" element={<GooglePickerPage />} />
            <Route path="/error" element={<ErrorPage />} />
        </Routes>
    );
};

export const DefaultCombinedRoutes = () => {
    return CombinedRoutes(RouteType.default);
};

export const TrialCombinedRoutes = () => {
    return CombinedRoutes(RouteType.trial);
};
